import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface AgentAccessTokenOptions {
  forceRefresh?: boolean;
}

export interface AgentAuthSession {
  sessionName?: string;
  accessToken: string | null;
  tokenLoading: boolean;
  tokenError: string | null;
  isAuthorizing: boolean;
  isAuthenticated: boolean;
  requiresLogin: boolean;
  authErrorMessage?: string;
  getAccessToken: (options?: AgentAccessTokenOptions) => Promise<string | null>;
  login: () => Promise<void>;
}

const AUTH0_LOADING_TIMEOUT_MS = 12_000;
const AUTH0_TOKEN_TIMEOUT_MS = 12_000;

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  label: string
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(
        new Error(
          `${label} timed out after ${Math.round(timeoutMs / 1000)} seconds.`
        )
      );
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timer);
        reject(error);
      });
  });
}

function getAuth0ErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const record = error as Record<string, unknown>;
  if (typeof record.error === "string" && record.error.length > 0) {
    return record.error;
  }

  if (typeof record.code === "string" && record.code.length > 0) {
    return record.code;
  }

  return null;
}

function isExpectedSilentAuthMiss(error: unknown): boolean {
  const code = getAuth0ErrorCode(error);
  return code === "login_required" || code === "consent_required";
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Authentication failed. Please sign in again.";
}

export function useAgentAuthSession(): AgentAuthSession {
  const { user, isLoading, error, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();
  const userSub = user?.sub;
  const [isBootstrappingAuth, setIsBootstrappingAuth] = useState(false);
  const [silentBootstrapError, setSilentBootstrapError] =
    useState<Error | null>(null);
  const [authSdkLoadingTimedOut, setAuthSdkLoadingTimedOut] = useState(false);
  const hasAttemptedSilentBootstrapRef = useRef(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const pendingTokenRequestRef = useRef<Promise<string | null> | null>(null);
  const pendingTokenRequestNonceRef = useRef<symbol | null>(null);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (!isLoading) {
      setAuthSdkLoadingTimedOut(false);
      return;
    }

    const timer = window.setTimeout(() => {
      if (isMountedRef.current) {
        setAuthSdkLoadingTimedOut(true);
      }
    }, AUTH0_LOADING_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    if (userSub) {
      hasAttemptedSilentBootstrapRef.current = false;
      setSilentBootstrapError(null);
      setAuthSdkLoadingTimedOut(false);
    }
  }, [userSub]);

  useEffect(() => {
    if (isLoading || userSub) {
      setIsBootstrappingAuth(false);
      return;
    }

    if (hasAttemptedSilentBootstrapRef.current) {
      return;
    }

    hasAttemptedSilentBootstrapRef.current = true;
    setIsBootstrappingAuth(true);

    let isCancelled = false;
    void withTimeout(
      getAccessTokenSilently({ cacheMode: "on" }),
      AUTH0_TOKEN_TIMEOUT_MS,
      "Auth0 silent authentication"
    )
      .then(() => {
        if (!isCancelled && isMountedRef.current) {
          setSilentBootstrapError(null);
        }
      })
      .catch((silentError) => {
        if (isCancelled || !isMountedRef.current) {
          return;
        }

        if (isExpectedSilentAuthMiss(silentError)) {
          setSilentBootstrapError(null);
          return;
        }

        const normalizedError =
          silentError instanceof Error
            ? silentError
            : new Error(String(silentError));
        setSilentBootstrapError(normalizedError);
      })
      .finally(() => {
        if (!isCancelled && isMountedRef.current) {
          setIsBootstrappingAuth(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [getAccessTokenSilently, isLoading, userSub]);

  const getAccessToken = useCallback(
    async (options?: AgentAccessTokenOptions): Promise<string | null> => {
      if (!userSub) {
        if (isMountedRef.current) {
          setAccessToken(null);
          setTokenLoading(false);
          setTokenError("Authentication required.");
        }
        return null;
      }

      const forceRefresh = options?.forceRefresh === true;

      if (!forceRefresh && pendingTokenRequestRef.current) {
        return pendingTokenRequestRef.current;
      }

      const requestNonce = Symbol("token-request");
      pendingTokenRequestNonceRef.current = requestNonce;

      const requestPromise = (async () => {
        if (isMountedRef.current) {
          setTokenLoading(true);
        }

        try {
          const token = await withTimeout(
            getAccessTokenSilently({
              cacheMode: forceRefresh ? "off" : "on"
            }),
            AUTH0_TOKEN_TIMEOUT_MS,
            "Auth0 token retrieval"
          );

          if (isMountedRef.current) {
            setAccessToken(token);
            setTokenLoading(false);
            setTokenError(null);
          }

          return token;
        } catch (tokenRetrievalError) {
          if (isMountedRef.current) {
            setAccessToken(null);
            setTokenLoading(false);
            setTokenError(getErrorMessage(tokenRetrievalError));
          }
          return null;
        } finally {
          if (pendingTokenRequestNonceRef.current === requestNonce) {
            pendingTokenRequestRef.current = null;
            pendingTokenRequestNonceRef.current = null;
          }
        }
      })();

      pendingTokenRequestRef.current = requestPromise;
      return requestPromise;
    },
    [getAccessTokenSilently, userSub]
  );

  useEffect(() => {
    if (!userSub) {
      setAccessToken(null);
      setTokenLoading(false);
      setTokenError("Authentication required.");
      pendingTokenRequestRef.current = null;
      pendingTokenRequestNonceRef.current = null;
      return;
    }

    void getAccessToken();
  }, [getAccessToken, userSub]);

  const authErrorMessage = useMemo(() => {
    if (authSdkLoadingTimedOut) {
      return "Authentication startup timed out. Please sign in again.";
    }

    if (error?.message) {
      return error.message;
    }

    if (silentBootstrapError?.message) {
      return silentBootstrapError.message;
    }

    return undefined;
  }, [authSdkLoadingTimedOut, error?.message, silentBootstrapError?.message]);

  const isAuthorizing =
    (isLoading && !authSdkLoadingTimedOut) ||
    isBootstrappingAuth ||
    (Boolean(userSub) && tokenLoading && !accessToken);
  const isAuthenticated = Boolean(userSub);
  const requiresLogin = !isAuthorizing && !isAuthenticated;

  return {
    accessToken,
    tokenLoading,
    tokenError,
    isAuthorizing,
    isAuthenticated,
    requiresLogin,
    getAccessToken,
    login: () => loginWithRedirect(),
    ...(userSub ? { sessionName: userSub } : {}),
    ...(authErrorMessage ? { authErrorMessage } : {})
  };
}
